/**
 * Multi-select location picker (Phase E.1).
 *
 * Replaces the single-country Select on the Ad campaigns tab. Operator
 * picks any number of Google Ads geo targets (state, DMA, city, postal
 * code) via autocomplete-backed search. Matches the UX of Google Ads
 * Keyword Planner's location modal.
 *
 * Phase E.2 will wire a Google Map preview below the chip list.
 * Phase E.3 will add polygon overlays + per-chip reach numbers.
 */
import { fuzzyFilter } from '@/lib/fuzzyMatch';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ExternalLink,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';

import { Link } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePatchBrandBibleServiceLocations } from '@/hooks/useBrandBible';
import type { BrandBibleServiceLocationsPatchResult } from '@/services/brandBibleService';
import {
  previewGeoTargetSuggestions,
  resolveGeoTargets,
  searchGeoTargets,
  type GeoTargetSuggestion,
  type SeedKeywordAutomationLog,
} from '@/services/googleAdsCampaignsService';
import { LocationMap } from './LocationMap';

// Modal needs to know how to add/remove from the parent's selected
// list (multi-select inside the modal updates in real time so the
// operator sees the map fill in as they pick).


/**
 * CORE-K-06: how many chips either strip paints inline before folding
 * the rest behind a "Show all N" button. Mirrors Campaign Settings'
 * TARGET_CHIP_CAP (CORE-S-01.e/f) — a tenant with 30 Brand Bible
 * markets no longer paints a wall of chips on first render.
 */
const LOCATION_CHIP_CAP = 5;

/**
 * CORE-K-08.b: the resolve endpoint accepts at most this many names per
 * request. Past it the "Use selected as targets" button is disabled with
 * a hint rather than sending a request that can only come back 422.
 */
export const RESOLVE_NAME_CAP = 50;


interface LocationPickerProps {
  tenantId: string;
  selected: GeoTargetSuggestion[];
  onChange: (next: GeoTargetSuggestion[]) => void;
  /**
   * CORE-K-05: Workspace-configured fallback locations (Brand Bible
   * `service_locations` + `locations` values). Rendered inline in the
   * empty-state chip strip so the operator can SEE which markets the
   * research will run against when they leave the picker blank —
   * replacing the vague "Auto (tenant default)" wording.
   *
   * Pass ONLY when the parent's downstream call actually falls back to
   * these names (e.g. AdCampaignKeywordsPanel's
   * `buildLocationSeedNames`). The sponsored-ads picker, which defaults
   * to US and never touches workspaceLocations, must not pass this
   * prop — otherwise the operator reads a fallback that will never
   * happen.
   */
  fallbackLocations?: readonly string[];
  /**
   * CORE-K-09: the subset of `fallbackLocations` that is Brand Bible
   * `service_locations` — the only rows the "Workspace default locations"
   * modal may rename / delete / add to. Rows that come from the city
   * groups (`locations`) are shown without edit controls. Leave undefined
   * to keep the modal exactly as before (no edit controls at all).
   */
  editableNames?: readonly string[];
}


export function LocationPicker({
  tenantId,
  selected,
  onChange,
  fallbackLocations,
  editableNames,
}: LocationPickerProps) {
  const [modalOpen, setModalOpen] = useState(false);
  // CORE-K-06: "Show all N" modal over the selected strip.
  const [listModalOpen, setListModalOpen] = useState(false);
  // CORE-K-08.b: "Show all N" modal over the workspace-defaults strip.
  // Owned here, not by EmptyLocationHint: the moment "Use selected as
  // targets" adds a location, `selected` becomes non-empty and the hint
  // unmounts — the modal has to outlive that so the operator sees the
  // result line.
  const [defaultsModalOpen, setDefaultsModalOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolveOutcome, setResolveOutcome] = useState<ResolveOutcome | null>(null);
  const resolveAbortRef = useRef<AbortController | null>(null);
  const workspaceNames = useMemo(
    () => trimNames(fallbackLocations),
    [fallbackLocations],
  );
  const editableWorkspaceNames = useMemo(
    () => (editableNames ? trimNames(editableNames) : undefined),
    [editableNames],
  );
  const totalReach = selected.reduce(
    (sum, loc) => sum + (loc.reach ?? 0),
    0,
  );

  const addLocation = (loc: GeoTargetSuggestion) => {
    if (selected.find((s) => s.resource_name === loc.resource_name)) {
      return;
    }
    onChange([...selected, loc]);
  };

  // Single removal path shared by the inline chip x, the search dialog
  // and the "Show all" list modal — every Remove lands on the same
  // onChange call, so no caller sees a different result.
  const removeLocation = (loc: GeoTargetSuggestion) => {
    onChange(selected.filter((s) => s.resource_name !== loc.resource_name));
  };

  // CORE-K-08.a: bulk Remove from the list modal — one onChange call for
  // the whole batch so the parent (and any effect keyed on `selected`)
  // sees a single transition instead of N.
  const removeLocations = (locs: readonly GeoTargetSuggestion[]) => {
    const ids = new Set(locs.map((l) => l.resource_name));
    onChange(selected.filter((s) => !ids.has(s.resource_name)));
  };

  const visibleSelected = selected.slice(0, LOCATION_CHIP_CAP);

  const closeDefaultsModal = () => {
    resolveAbortRef.current?.abort();
    resolveAbortRef.current = null;
    setDefaultsModalOpen(false);
    setResolving(false);
    setResolveOutcome(null);
  };

  // Abort an in-flight resolve if the picker itself goes away.
  useEffect(() => () => resolveAbortRef.current?.abort(), []);

  // CORE-K-08.b: resolve checked Brand Bible names to geo targets and
  // merge them into `selected` (deduped by resource_name) in ONE onChange.
  // Returns true on success so the modal can clear its checked set; on
  // failure the checks stay so the operator can retry.
  const useNamesAsTargets = async (names: string[]): Promise<boolean> => {
    resolveAbortRef.current?.abort();
    const ctl = new AbortController();
    resolveAbortRef.current = ctl;
    setResolving(true);
    setResolveOutcome(null);
    try {
      const result = await resolveGeoTargets({
        tenantId,
        names,
        signal: ctl.signal,
      });
      if (ctl.signal.aborted) return false;
      const have = new Set(selected.map((s) => s.resource_name));
      const added: GeoTargetSuggestion[] = [];
      for (const { target } of result.resolved) {
        if (have.has(target.resource_name)) continue;
        have.add(target.resource_name);
        added.push(target);
      }
      onChange([...selected, ...added]);
      setResolveOutcome({
        kind: 'ok',
        added: added.length,
        unmatched: result.unmatched,
      });
      return true;
    } catch {
      if (ctl.signal.aborted) return false;
      setResolveOutcome({ kind: 'error' });
      return false;
    } finally {
      if (!ctl.signal.aborted) setResolving(false);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-medium text-muted-foreground">
          Location
        </Label>
        {selected.length > 0 && totalReach > 0 && (
          <span className="text-[11px] text-muted-foreground">
            Total reach:{' '}
            <span className="font-medium text-teal-dark">
              {formatCompact(totalReach)}
            </span>{' '}
            across {selected.length} location
            {selected.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Selected chips + add button */}
      <div className="flex flex-wrap items-center gap-1.5 rounded border border-border bg-card p-2 min-h-[44px]">
        {selected.length === 0 && (
          // CORE-K-05: name the ACTUAL fallback markets when the
          // parent falls back on workspace defaults. Reading "Auto
          // (tenant default)" left the operator guessing which
          // markets the research would run against; now they can see
          // the names ("Kirkwood, MO", "St. Louis, MO", ...) and
          // recognise them (or override with "Add location" if they
          // don't).
          <EmptyLocationHint
            names={workspaceNames}
            onShowAll={() => setDefaultsModalOpen(true)}
          />
        )}
        {visibleSelected.map((loc) => (
          <LocationChip
            key={loc.resource_name}
            location={loc}
            onRemove={() => removeLocation(loc)}
          />
        ))}
        {selected.length > LOCATION_CHIP_CAP && (
          <ShowAllButton
            total={selected.length}
            onClick={() => setListModalOpen(true)}
          />
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setModalOpen(true)}
          className="h-7 gap-1 text-xs"
        >
          <Plus className="h-3 w-3" />
          Add location
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Countries, states, DMAs (metros), cities, or postal codes.
        Leave it empty and we use the workspace's locations.
      </p>

      <LocationSearchDialog
        tenantId={tenantId}
        open={modalOpen}
        selected={selected}
        onOpenChange={setModalOpen}
        onPick={addLocation}
        onRemove={removeLocation}
      />

      {listModalOpen && (
        <LocationListModal
          title="Selected locations"
          entries={selected}
          onRemove={removeLocation}
          onRemoveMany={removeLocations}
          onClose={() => setListModalOpen(false)}
        />
      )}

      {defaultsModalOpen && (
        <WorkspaceDefaultsModal
          tenantId={tenantId}
          entries={workspaceNames}
          editableNames={editableWorkspaceNames}
          onUseSelected={useNamesAsTargets}
          resolving={resolving}
          resolveOutcome={resolveOutcome}
          onClose={closeDefaultsModal}
        />
      )}
    </div>
  );
}


/**
 * CORE-K-09: thin wrapper that owns the Brand Bible write hook for the
 * "Workspace default locations" modal. Kept out of LocationPicker itself
 * so the picker needs no query client until this modal is actually open,
 * and mounted only while open so the modal's confirm-once state re-arms
 * every time it is reopened.
 */
function WorkspaceDefaultsModal({
  tenantId,
  entries,
  editableNames,
  onUseSelected,
  resolving,
  resolveOutcome,
  onClose,
}: {
  tenantId: string;
  entries: readonly string[];
  editableNames?: readonly string[];
  onUseSelected: (names: string[]) => Promise<boolean>;
  resolving: boolean;
  resolveOutcome: ResolveOutcome | null;
  onClose: () => void;
}) {
  const patch = usePatchBrandBibleServiceLocations(tenantId);
  return (
    <LocationListModal
      title="Workspace default locations"
      entries={entries}
      editableNames={editableNames}
      onSaveNames={editableNames ? (next) => patch.mutateAsync(next) : undefined}
      onUseSelected={onUseSelected}
      resolving={resolving}
      resolveOutcome={resolveOutcome}
      onClose={onClose}
    />
  );
}


/** Trim Brand Bible names and drop blanks (shared by the strip and its modal). */
function trimNames(names?: readonly string[]): string[] {
  return (names ?? [])
    .map((v) => v?.trim())
    .filter((v): v is string => Boolean(v));
}


/** CORE-K-08.b: what the last "Use selected as targets" call produced. */
type ResolveOutcome =
  | { kind: 'ok'; added: number; unmatched: string[] }
  | { kind: 'error' };


// CORE-K-05: named component (bus-factor policy §8: named files/components
// beat clever inline JSX). Two branches:
//   - With fallback names: shows a "Using workspace defaults:" prefix and
//     renders each name as its own read-only chip so the operator can see
//     what the research will actually target.
//   - Without: falls back to the old italic string. This branch runs when
//     the parent workspace has no Brand Bible service_locations or
//     locations configured yet — a legitimate state the picker should not
//     lie about (still calls it "tenant default" because at that point
//     there truly IS no configured value to name).
//
// CORE-K-08.b: receives already-trimmed names and only paints chips + the
// "Show all" button; the modal itself lives in LocationPicker (see
// `defaultsModalOpen`) because this component unmounts as soon as a
// location is selected.
function EmptyLocationHint({
  names,
  onShowAll,
}: {
  names: readonly string[];
  onShowAll: () => void;
}) {
  const trimmed = names;

  if (trimmed.length === 0) {
    return (
      <span className="text-xs italic text-muted-foreground">
        No workspace default set — click "Add location" to pick specific
        markets
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">
        Using workspace defaults:
      </span>
      {trimmed.slice(0, LOCATION_CHIP_CAP).map((name) => (
        <Badge
          key={name}
          variant="outline"
          className="bg-warm-bg text-[11px] font-normal text-teal-dark"
          aria-label={`Workspace default location: ${name}`}
        >
          <MapPin className="mr-1 h-3 w-3" />
          {name}
        </Badge>
      ))}
      {trimmed.length > LOCATION_CHIP_CAP && (
        <ShowAllButton total={trimmed.length} onClick={onShowAll} />
      )}
    </div>
  );
}


// CORE-K-06: the "Show all N" text button that follows a capped chip strip.
// Same look as Campaign Settings' TargetChipList so the two pages read as
// one pattern.
function ShowAllButton({
  total,
  onClick,
}: {
  total: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[11px] font-medium text-coral-dark hover:underline"
      aria-haspopup="dialog"
    >
      Show all {total}
    </button>
  );
}


/**
 * CORE-K-06: full-list modal opened from "Show all N". Serves both strips:
 *   - workspace defaults (plain strings, no actions, plus a link to the
 *     Brand Bible where those names are edited), and
 *   - selected geo targets (name + type, with a Remove that goes through the
 *     parent's `onRemove` — the same onChange path as the inline chip x).
 * Sibling of Campaign Settings' TargetListModal rather than a reuse of it:
 * that one is typed to TargetingRow and carries an Included/Excluded toggle
 * this list has no use for.
 *
 * CORE-K-08.a: when `onRemoveMany` is given, every row gets a checkbox, the
 * header gets a tri-state "Select all shown" (filter-aware), and the footer
 * gets "Remove selected (N)" which hands the whole batch to the parent in
 * one call. Checked state is a Set of row keys (resource_name for geo
 * targets, the name itself for workspace defaults), always intersected
 * with the current entries at render so a per-row Remove or an outside
 * change never leaves a ghost in the count.
 *
 * CORE-K-08.b: the workspace-defaults mode (`onUseSelected`) shares the
 * same selection UI; its footer action is "Use selected as targets (N)",
 * which hands the checked names to the parent to resolve into geo targets.
 * The parent owns the request + outcome (`resolving`, `resolveOutcome`)
 * so the modal can outlive the strip that opened it.
 *
 * CORE-K-09: with `editableNames` + `onSaveNames`, rows whose name is in
 * `editableNames` get inline Rename and Delete, an Add input sits above
 * the list, and the footer gets "Delete selected (N)". Every write sends
 * ONE patch with the full expected new list and goes through one confirm
 * per modal session (bulk delete always confirms). Nothing is optimistic:
 * the strip re-renders from the refetched Brand Bible.
 */
function LocationListModal({
  title,
  entries,
  onRemove,
  onRemoveMany,
  onUseSelected,
  resolving = false,
  resolveOutcome = null,
  editableNames,
  onSaveNames,
  onClose,
}: {
  title: string;
  entries: readonly string[] | readonly GeoTargetSuggestion[];
  onRemove?: (loc: GeoTargetSuggestion) => void;
  onRemoveMany?: (locs: GeoTargetSuggestion[]) => void;
  /** Resolves checked names; returns true when the batch went through. */
  onUseSelected?: (names: string[]) => Promise<boolean>;
  resolving?: boolean;
  resolveOutcome?: ResolveOutcome | null;
  /** CORE-K-09: names that may be renamed / deleted (Brand Bible service_locations). */
  editableNames?: readonly string[];
  /** CORE-K-09: writes the full expected new service_locations list. */
  onSaveNames?: (next: string[]) => Promise<BrandBibleServiceLocationsPatchResult>;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [checkedKeys, setCheckedKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // CORE-K-09 edit state. Lives here (the modal unmounts on close) so
  // `confirmArmed` re-arms every time the modal is reopened.
  const [renaming, setRenaming] = useState<{ key: string; value: string } | null>(null);
  const [addValue, setAddValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus | null>(null);
  const [confirmArmed, setConfirmArmed] = useState(false);
  const [pendingWrite, setPendingWrite] = useState<PendingWrite | null>(null);
  const [duplicateNotice, setDuplicateNotice] = useState(false);
  // The server's authoritative list after each write. Until the
  // invalidated Brand Bible refetches, `editableNames` is stale; basing
  // the next action on it would re-send the pre-write list and silently
  // undo the previous one. Reset whenever the prop itself changes.
  const [serverNames, setServerNames] = useState<string[] | null>(null);
  const [seenEditableNames, setSeenEditableNames] = useState(editableNames);
  if (editableNames !== seenEditableNames) {
    setSeenEditableNames(editableNames);
    setServerNames(null);
  }
  const selectable = Boolean(onRemoveMany || onUseSelected);
  const isWorkspaceDefaults = !onRemove && !onRemoveMany;
  const editing = Boolean(editableNames && onSaveNames);
  const baseNames = useMemo(
    () => serverNames ?? editableNames ?? [],
    [serverNames, editableNames],
  );
  const propEditableSet = useMemo(
    () => new Set(editableNames ?? []),
    [editableNames],
  );
  const baseSet = useMemo(() => new Set(baseNames), [baseNames]);

  // Workspace-defaults rows come from the LIVE base (server list after a
  // write) plus the locked city-group rows from the strip, so a deleted or
  // renamed row changes immediately instead of after the refetch.
  const rows = useMemo(() => {
    if (editing) {
      const locked = (entries as readonly string[]).filter(
        (entry) => !propEditableSet.has(entry) && !baseSet.has(entry),
      );
      return [
        ...baseNames.map((name) => ({
          key: name,
          name,
          loc: undefined,
          editable: true,
        })),
        ...locked.map((name) => ({
          key: name,
          name,
          loc: undefined,
          editable: false,
        })),
      ];
    }
    return entries.map((entry) =>
      typeof entry === 'string'
        ? { key: entry, name: entry, loc: undefined, editable: false }
        : {
            key: entry.resource_name,
            name: entry.name,
            loc: entry,
            editable: false,
          },
    );
  }, [entries, editing, baseNames, baseSet, propEditableSet]);
  const hasLockedRows = editing && rows.some((r) => !r.editable);

  // Fuzzy search, best match first (lib/fuzzyMatch); the list has no user sort.
  const filtered = useMemo(
    () => fuzzyFilter(rows, query, (row) => [row.name, row.loc?.canonical_name]),
    [rows, query],
  );

  // Live checked set = stored keys ∩ current entries. Derived, not synced
  // via effect, so it can never lag a removal.
  const checked = useMemo(() => {
    const present = new Set(rows.map((r) => r.key));
    return new Set([...checkedKeys].filter((k) => present.has(k)));
  }, [checkedKeys, rows]);

  const visibleKeys = filtered.map((r) => r.key);
  const visibleCheckedCount = visibleKeys.filter((k) => checked.has(k)).length;
  const headerState: boolean | 'indeterminate' =
    visibleKeys.length > 0 && visibleCheckedCount === visibleKeys.length
      ? true
      : visibleCheckedCount > 0
        ? 'indeterminate'
        : false;

  const toggleRow = (key: string, next: boolean) => {
    setCheckedKeys((prev) => {
      const out = new Set(prev);
      if (next) out.add(key);
      else out.delete(key);
      return out;
    });
  };

  // Header toggle acts on exactly the visible (filtered) rows: fully
  // checked -> uncheck them; anything else -> check them all.
  const toggleVisible = () => {
    setCheckedKeys((prev) => {
      const out = new Set(prev);
      if (headerState === true) visibleKeys.forEach((k) => out.delete(k));
      else visibleKeys.forEach((k) => out.add(k));
      return out;
    });
  };

  // Per-row Remove also drops the key from the stored set (not only the
  // render-time mask) so a location removed and later re-added never comes
  // back pre-checked.
  const removeOne = (loc: GeoTargetSuggestion) => {
    toggleRow(loc.resource_name, false);
    onRemove?.(loc);
  };

  const removeChecked = () => {
    if (!onRemoveMany || checked.size === 0) return;
    const batch = rows
      .filter((r) => r.loc && checked.has(r.key))
      .map((r) => r.loc as GeoTargetSuggestion);
    onRemoveMany(batch);
    setCheckedKeys(new Set());
  };

  const overResolveCap = Boolean(onUseSelected) && checked.size > RESOLVE_NAME_CAP;

  const useChecked = async () => {
    if (!onUseSelected || checked.size === 0 || resolving || overResolveCap) return;
    const names = rows.filter((r) => checked.has(r.key)).map((r) => r.name);
    const ok = await onUseSelected(names);
    if (ok) setCheckedKeys(new Set());
  };

  // ---- CORE-K-09 writes -------------------------------------------------
  // Every action computes the FULL expected new service_locations list
  // from `editableNames` and sends it as one patch. `busy` freezes every
  // control while a write or a resolve is in flight.
  const busy = saving || resolving;
  const checkedEditableNames = rows
    .filter((r) => r.editable && checked.has(r.key))
    .map((r) => r.name);

  const runWrite = async (next: string[]) => {
    if (!onSaveNames) return;
    setSaving(true);
    setSaveStatus(null);
    try {
      const result = await onSaveNames(next);
      setServerNames(result.service_locations);
      setSaveStatus({
        kind: 'ok',
        added: result.added.length,
        removed: result.removed.length,
      });
      setRenaming(null);
      setAddValue('');
      // Rows the server dropped can no longer be checked.
      const gone = new Set(result.removed);
      setCheckedKeys((prev) => new Set([...prev].filter((k) => !gone.has(k))));
    } catch {
      setSaveStatus({ kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // One confirm per modal session; bulk delete always confirms.
  const requestWrite = (write: PendingWrite, alwaysConfirm = false) => {
    if (!onSaveNames || busy) return;
    if (confirmArmed && !alwaysConfirm) {
      void runWrite(write.next);
      return;
    }
    setPendingWrite(write);
  };

  const applyPendingWrite = () => {
    const write = pendingWrite;
    setPendingWrite(null);
    setConfirmArmed(true);
    if (write) void runWrite(write.next);
  };

  const deleteName = (name: string) => {
    requestWrite({
      affected: [name],
      next: baseNames.filter((n) => n !== name),
    });
  };

  const deleteChecked = () => {
    if (checkedEditableNames.length === 0) return;
    const gone = new Set(checkedEditableNames);
    requestWrite(
      {
        affected: checkedEditableNames,
        next: baseNames.filter((n) => !gone.has(n)),
      },
      true,
    );
  };

  // Refuse a name already in the list (case-insensitive) before sending:
  // the server would dedupe it and, on a rename, silently drop the row.
  const isTaken = (name: string, except?: string) => {
    const key = name.toLowerCase();
    return baseNames.some((n) => n !== except && n.toLowerCase() === key);
  };

  const commitRename = () => {
    if (!renaming) return;
    const from = renaming.key;
    const to = renaming.value.trim();
    if (!to || to === from) {
      setRenaming(null);
      return;
    }
    if (isTaken(to, from)) {
      setDuplicateNotice(true);
      return;
    }
    setDuplicateNotice(false);
    requestWrite({
      affected: [`${from} → ${to}`],
      next: baseNames.map((n) => (n === from ? to : n)),
    });
  };

  const addName = () => {
    const name = addValue.trim();
    if (!name) return;
    if (isTaken(name)) {
      setDuplicateNotice(true);
      return;
    }
    setDuplicateNotice(false);
    requestWrite({ affected: [name], next: [...baseNames, name] });
  };

  return (
    <Dialog open onOpenChange={(next) => (!next ? onClose() : undefined)}>
      {/* The shared Dialog wrapper renders no landmark role of its own;
          DialogContent forwards extra props to its div, so the role and
          label are set here so assistive tech announces the list. */}
      <DialogContent
        className="max-w-3xl"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {rows.length.toLocaleString()} location{rows.length === 1 ? '' : 's'}
            {selectable && checked.size > 0
              ? ` · ${checked.size.toLocaleString()} selected`
              : ''}
          </DialogDescription>
        </DialogHeader>

        <Input
          type="search"
          placeholder="Search locations…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-8"
          aria-label="Search locations"
        />

        {editing && (
          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="Add a workspace location"
              aria-label="Add a workspace location"
              value={addValue}
              disabled={busy}
              onChange={(e) => {
                setAddValue(e.target.value);
                setDuplicateNotice(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addName();
                }
              }}
              className="h-8"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy || !addValue.trim()}
              onClick={addName}
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
              Add
            </Button>
          </div>
        )}

        <div className="max-h-[60vh] overflow-y-auto rounded-md border border-border">
          {selectable && filtered.length > 0 && (
            <div className="flex items-center gap-2 border-b border-border bg-muted px-3 py-2 text-xs font-medium text-muted-foreground">
              <Checkbox
                checked={headerState}
                onCheckedChange={toggleVisible}
                disabled={busy}
                aria-label="Select all shown"
              />
              <span>
                {visibleCheckedCount.toLocaleString()} of{' '}
                {visibleKeys.length.toLocaleString()} shown selected
              </span>
            </div>
          )}
          {rows.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">
              No locations selected.
            </p>
          ) : filtered.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">
              No locations match.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((row) => (
                <li
                  key={row.key}
                  className="flex items-center gap-2 px-3 py-2 text-sm"
                >
                  {selectable && (
                    <Checkbox
                      checked={checked.has(row.key)}
                      onCheckedChange={(next) => toggleRow(row.key, next === true)}
                      disabled={busy}
                      aria-label={`Select ${row.name}`}
                    />
                  )}
                  <MapPin
                    className="h-3.5 w-3.5 flex-shrink-0 text-teal"
                    aria-hidden="true"
                  />
                  {renaming?.key === row.key ? (
                    <Input
                      type="text"
                      autoFocus
                      value={renaming.value}
                      disabled={busy}
                      aria-label={`New name for ${row.name}`}
                      onChange={(e) => {
                        setRenaming({ key: row.key, value: e.target.value });
                        setDuplicateNotice(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitRename();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setRenaming(null);
                        }
                      }}
                      className="h-7 flex-1"
                    />
                  ) : (
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-medium">{row.name}</span>
                      {row.loc && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({row.loc.target_type})
                        </span>
                      )}
                    </span>
                  )}
                  {row.editable && (
                    <>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setRenaming({ key: row.key, value: row.name })}
                        className="rounded-full p-1 text-muted-foreground hover:bg-coral-light hover:text-coral-dark disabled:opacity-50"
                        aria-label={`Rename ${row.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => deleteName(row.name)}
                        className="rounded-full p-1 text-muted-foreground hover:bg-coral-light hover:text-destructive disabled:opacity-50"
                        aria-label={`Delete ${row.name}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </>
                  )}
                  {row.loc && onRemove && (
                    <button
                      type="button"
                      onClick={() => removeOne(row.loc as GeoTargetSuggestion)}
                      className="rounded-full p-1 text-muted-foreground hover:bg-coral-light hover:text-coral-dark"
                      aria-label={`Remove ${row.name}`}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {resolveOutcome?.kind === 'ok' && (
          <div role="status" className="space-y-0.5 text-xs text-muted-foreground">
            <p>Added {resolveOutcome.added.toLocaleString()} location(s).</p>
            {resolveOutcome.unmatched.length > 0 && (
              <p>No match for: {resolveOutcome.unmatched.join(', ')}</p>
            )}
          </div>
        )}
        {resolveOutcome?.kind === 'error' && (
          <p role="alert" className="text-xs text-destructive">
            Couldn't resolve locations. Try again.
          </p>
        )}

        {saveStatus?.kind === 'ok' && (
          <p className="text-xs text-muted-foreground">
            Saved. {saveStatus.added.toLocaleString()} added,{' '}
            {saveStatus.removed.toLocaleString()} removed.
          </p>
        )}
        {duplicateNotice && (
          <p className="text-xs text-muted-foreground">Already in the list.</p>
        )}
        {saveStatus?.kind === 'error' && (
          <p className="text-xs text-destructive">
            Couldn't save workspace locations. Try again.
          </p>
        )}
        {hasLockedRows && (
          <p className="text-xs text-muted-foreground">
            Rows from city groups are edited in Brand Bible.
          </p>
        )}

        {isWorkspaceDefaults && (
          <Link
            to="/brand-bible"
            className="text-xs text-coral-dark hover:underline"
          >
            Edit workspace locations in Brand Bible
          </Link>
        )}

        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            Showing {filtered.length.toLocaleString()} of{' '}
            {rows.length.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            {onRemoveMany && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-destructive"
                disabled={checked.size === 0}
                onClick={removeChecked}
              >
                Remove selected ({checked.size.toLocaleString()})
              </Button>
            )}
            {editing && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-destructive"
                disabled={checkedEditableNames.length === 0 || busy}
                onClick={deleteChecked}
              >
                Delete selected ({checkedEditableNames.length.toLocaleString()})
              </Button>
            )}
            {onUseSelected && (
              <div className="flex flex-col items-end gap-1">
                <Button
                  type="button"
                  size="sm"
                  className="bg-teal hover:bg-teal-hover"
                  disabled={checked.size === 0 || busy || overResolveCap}
                  onClick={useChecked}
                >
                  {resolving && (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                  )}
                  {resolving
                    ? 'Resolving…'
                    : `Use selected as targets (${checked.size.toLocaleString()})`}
                </Button>
                {overResolveCap && (
                  <span className="text-xs text-muted-foreground">
                    Pick up to {RESOLVE_NAME_CAP} at a time.
                  </span>
                )}
              </div>
            )}
            <Button type="button" size="sm" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        {/* CORE-K-09 confirm. AlertDialog portals to document.body, so it
            escapes this transformed DialogContent. */}
        <AlertDialog
          open={pendingWrite !== null}
          onOpenChange={(next) => (!next ? setPendingWrite(null) : undefined)}
        >
          <AlertDialogContent role="alertdialog" aria-modal="true">
            <AlertDialogHeader>
              <AlertDialogTitle>This changes the Brand Bible</AlertDialogTitle>
              <AlertDialogDescription>
                Workspace locations are used by content generation, location
                pages, GBP audits, negative-keyword context and schema.
                Changing them here changes them everywhere. Already-generated
                content is not updated.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <ul className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border border-border bg-muted p-2 text-sm">
              {(pendingWrite?.affected ?? []).map((name) => (
                <li key={name} className="truncate">{name}</li>
              ))}
            </ul>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setPendingWrite(null)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={applyPendingWrite}>
                Apply
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}


/** CORE-K-09: outcome of the last Brand Bible write from the modal. */
type SaveStatus =
  | { kind: 'ok'; added: number; removed: number }
  | { kind: 'error' };

/** CORE-K-09: a write waiting on the confirm dialog. */
type PendingWrite = { affected: string[]; next: string[] };


function LocationChip({
  location,
  onRemove,
}: {
  location: GeoTargetSuggestion;
  onRemove: () => void;
}) {
  const reach = location.reach;
  return (
    <Badge
      variant="outline"
      className="gap-1.5 bg-warm-bg pr-1 text-xs font-normal"
      title={
        reach && reach > 0
          ? `${location.canonical_name} — Google audience reach ${reach.toLocaleString()}`
          : location.canonical_name
      }
    >
      <MapPin className="h-3 w-3 text-teal" />
      <span className="font-medium">{location.name}</span>
      <span className="text-[10px] text-muted-foreground">
        ({location.target_type})
      </span>
      {reach != null && reach > 0 && (
        <span className="text-[10px] font-medium text-teal-dark">
          · {formatCompact(reach)}
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 rounded-full p-0.5 hover:bg-coral-light"
        aria-label={`Remove ${location.name}`}
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </Badge>
  );
}


/** Format a population number compactly: 4_700_000 → "4.7M". */
function formatCompact(n: number): string {
  try {
    return new Intl.NumberFormat('en', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(n);
  } catch {
    return n.toLocaleString();
  }
}


// ---------------------------------------------------------------------------
// Search dialog
// ---------------------------------------------------------------------------


function LocationSearchDialog({
  tenantId,
  open,
  selected,
  onOpenChange,
  onPick,
  onRemove,
}: {
  tenantId: string;
  open: boolean;
  selected: GeoTargetSuggestion[];
  onOpenChange: (open: boolean) => void;
  onPick: (loc: GeoTargetSuggestion) => void;
  onRemove: (loc: GeoTargetSuggestion) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeoTargetSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<LocationSearchError | null>(null);
  const [suggestedLocations, setSuggestedLocations] = useState<GeoTargetSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<LocationSearchError | null>(null);
  const [suggestionLogs, setSuggestionLogs] = useState<SeedKeywordAutomationLog[]>([]);
  const [suggestionProgress, setSuggestionProgress] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!query || query.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    abortRef.current?.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    const t = setTimeout(async () => {
      try {
        const r = await searchGeoTargets({
          tenantId,
          query: query.trim(),
          signal: ctl.signal,
        });
        setResults(r.results);
      } catch (err: unknown) {
        if (!ctl.signal.aborted) {
          setError(toLocationSearchError(err, tenantId));
          setResults([]);
        }
      } finally {
        if (!ctl.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => {
      clearTimeout(t);
      ctl.abort();
    };
  }, [query, tenantId, open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setError(null);
      setLoading(false);
      setSuggestionsError(null);
    }
  }, [open]);

  useEffect(() => {
    setSuggestedLocations([]);
    setSuggestionsLoaded(false);
    setSuggestionsError(null);
    setSuggestionLogs([]);
    setSuggestionProgress(0);
  }, [tenantId]);

  useEffect(() => {
    if (!open || suggestionsLoaded) return;
    let cancelled = false;
    const controller = new AbortController();
    const startedAt = new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    setSuggestionsLoading(true);
    setSuggestionsError(null);
    setSuggestionProgress(8);
    setSuggestionLogs([
      {
        stage: 'start',
        status: 'info',
        message: `Started workspace location suggestions at ${startedAt}.`,
      },
      {
        stage: 'workspace',
        status: 'info',
        message: 'Reading workspace settings and Brand Bible locations.',
      },
      {
        stage: 'google_ads',
        status: 'info',
        message: 'Resolving candidates in the Google Ads geo catalog.',
      },
    ]);
    const progressTimer = window.setInterval(() => {
      setSuggestionProgress((current) => Math.min(current + 7, 92));
    }, 700);
    const hardTimeout = window.setTimeout(() => {
      if (cancelled) return;
      controller.abort();
      setSuggestionsError({
        title: 'Location suggestions timed out',
        message:
          'Workspace suggestions took too long. Use custom search above, or try again in a minute.',
      });
      setSuggestionLogs((logs) => [
        ...logs,
        {
          stage: 'timeout',
          status: 'warning',
          message: 'Stopped waiting for workspace location suggestions.',
          detail: 'Manual search remains available.',
        },
      ]);
      setSuggestionsLoaded(true);
      setSuggestionsLoading(false);
      setSuggestionProgress(100);
    }, 12_000);

    previewGeoTargetSuggestions({ tenantId, limit: 12, signal: controller.signal })
      .then((result) => {
        if (cancelled) return;
        window.clearTimeout(hardTimeout);
        setSuggestedLocations(result.suggestions);
        setSuggestionLogs(result.logs);
        setSuggestionsLoaded(true);
        setSuggestionProgress(100);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        window.clearTimeout(hardTimeout);
        if (controller.signal.aborted) return;
        setSuggestionsError(toLocationSearchError(err, tenantId));
        setSuggestionLogs((logs) => [
          ...logs,
          {
            stage: 'error',
            status: 'warning',
            message: 'Location suggestion automation stopped before returning options.',
            detail: extractApiErrorDetail(err),
          },
        ]);
        setSuggestionsLoaded(true);
        setSuggestionProgress(100);
      })
      .finally(() => {
        window.clearInterval(progressTimer);
        if (!cancelled) setSuggestionsLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(progressTimer);
      window.clearTimeout(hardTimeout);
    };
  }, [open, suggestionsLoaded, tenantId]);

  const selectedKeys = useMemo(
    () => new Set(selected.map((s) => s.resource_name)),
    [selected],
  );
  const retryLocationSuggestions = () => {
    setSuggestedLocations([]);
    setSuggestionLogs([]);
    setSuggestionsError(null);
    setSuggestionsLoaded(false);
    setSuggestionProgress(0);
  };
  const pickLocation = (loc: GeoTargetSuggestion) => {
    if (selectedKeys.has(loc.resource_name)) {
      return;
    }
    onPick(loc);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Wider modal — left column = search + selected list, right
          column = map. Matches Google Ads Keyword Planner UX. */}
      <DialogContent className="flex max-h-[92vh] max-w-5xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-teal" />
            Add a location
          </DialogTitle>
          <DialogDescription>
            Search for any country, state, metro (DMA), city, or postal
            code. Selections persist as you search.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* LEFT — search + selected list */}
          <div className="space-y-3">
            <div className="rounded border border-border bg-card p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <Label className="text-xs font-medium text-muted-foreground">
                  Search custom location
                </Label>
                <span className="text-[11px] text-muted-foreground">
                  city, state, DMA, ZIP, or country
                </span>
              </div>
              <Input
                type="text"
                autoFocus
                placeholder='Try "St. Louis", "Missouri", "90210"…'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <p className="mt-2 text-[11px] text-muted-foreground">
                Suggested locations are optional. You can always search
                and add a custom Google Ads geo target.
              </p>

            {/* Search results */}
              <div className="mt-3 max-h-56 overflow-y-auto rounded border border-border">
              {loading && (
                <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Searching…
                </div>
              )}
              {!loading && error && (
                <LocationSearchErrorPanel error={error} />
              )}
              {!loading && !error && query.trim().length < 2 && (
                <div className="p-4 text-sm text-muted-foreground">
                  Type at least 2 characters to search.
                </div>
              )}
              {!loading && !error && query.trim().length >= 2 && results.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground">
                  No matches for <code>{query}</code>.
                </div>
              )}
              {!loading && !error && results.length > 0 && (
                <ul className="divide-y divide-border">
                  {results.map((r) => {
                    const isSelected = selectedKeys.has(r.resource_name);
                    return (
                      <li key={r.resource_name}>
                        <button
                          type="button"
                          disabled={isSelected}
                          onClick={() => {
                            pickLocation(r);
                          }}
                          className={
                            'flex w-full items-center justify-between gap-3 p-3 text-left ' +
                            (isSelected
                              ? 'cursor-not-allowed bg-warm-bg opacity-60'
                              : 'hover:bg-warm-bg')
                          }
                        >
                          <div className="flex-1">
                            <div className="font-medium">{r.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {r.canonical_name}
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge
                              variant="outline"
                              className="text-[10px] font-normal"
                            >
                              {r.target_type}
                            </Badge>
                            {r.reach != null && r.reach > 0 && (
                              <div className="mt-0.5 text-[10px] text-muted-foreground">
                                reach {r.reach.toLocaleString()}
                              </div>
                            )}
                            {isSelected && (
                              <div className="mt-0.5 text-[10px] text-teal-dark">
                                already added
                              </div>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              </div>
            </div>

            <WorkspaceLocationSuggestions
              loading={suggestionsLoading}
              error={suggestionsError}
              suggestions={suggestedLocations}
              logs={suggestionLogs}
              progress={suggestionProgress}
              selectedKeys={selectedKeys}
              onPick={pickLocation}
              onRetry={retryLocationSuggestions}
            />

            {/* Currently selected — operator can remove without
                closing the modal. Layout mirrors Google's: full
                canonical name on the left, prominent reach on the
                right. */}
            {selected.length > 0 && (
              <div className="rounded border border-border">
                <div className="flex items-center justify-between border-b border-border bg-warm-bg px-3 py-2 text-xs font-medium">
                  <span>Locations ({selected.length})</span>
                  <span className="text-muted-foreground">Reach</span>
                </div>
                <ul className="max-h-40 divide-y divide-border overflow-y-auto">
                  {selected.map((s) => (
                    <li
                      key={s.resource_name}
                      className="flex items-center gap-3 px-3 py-2 text-sm"
                    >
                      <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-teal" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">
                          {s.canonical_name || s.name}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {s.target_type}
                        </div>
                      </div>
                      <div className="text-right tabular-nums">
                        {s.reach && s.reach > 0 ? (
                          <div className="text-sm font-medium text-teal-dark">
                            {s.reach.toLocaleString()}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => onRemove(s)}
                        className="ml-1 rounded-full p-1 text-muted-foreground hover:bg-coral-light hover:text-coral-dark"
                        aria-label={`Remove ${s.name}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* RIGHT — live map showing selected locations */}
          <div className="space-y-2">
            <LocationMap
              tenantId={tenantId}
              selected={selected}
              onPickLocation={pickLocation}
            />
          </div>
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-between border-t border-border bg-card pt-3 text-xs text-muted-foreground">
          <span>
            {selected.length} location{selected.length !== 1 ? 's' : ''} selected
          </span>
          <Button
            type="button"
            size="sm"
            className="bg-teal hover:bg-teal-hover"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function WorkspaceLocationSuggestions({
  loading,
  error,
  suggestions,
  logs,
  progress,
  selectedKeys,
  onPick,
  onRetry,
}: {
  loading: boolean;
  error: LocationSearchError | null;
  suggestions: GeoTargetSuggestion[];
  logs: SeedKeywordAutomationLog[];
  progress: number;
  selectedKeys: Set<string>;
  onPick: (loc: GeoTargetSuggestion) => void;
  onRetry: () => void;
}) {
  const header = (
    <div className="flex items-center gap-2 border-b border-teal/20 px-3 py-2 text-xs font-medium text-teal-dark">
      <Sparkles className="h-3.5 w-3.5" />
      Suggested location options
    </div>
  );

  if (loading) {
    return (
      <div className="rounded border border-dashed border-teal/40 bg-warm-bg">
        {header}
        <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Resolving workspace locations…
        </div>
        <LocationSuggestionProgress progress={progress} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded border border-dashed border-teal/40 bg-warm-bg">
        {header}
        <LocationSearchErrorPanel error={error} compact />
        <div className="px-3 pb-3">
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>
            <RefreshCw className="h-3 w-3" />
            Retry suggestions
          </Button>
        </div>
        <LocationSuggestionProgress progress={100} />
      </div>
    );
  }

  if (suggestions.length === 0) {
    const emptyLog =
      [...logs].reverse().find((log) => log.status === 'warning')
      ?? logs[logs.length - 1];

    return (
      <div className="rounded border border-dashed border-teal/40 bg-warm-bg">
        {header}
        <div className="space-y-3 px-3 py-3 text-sm text-muted-foreground">
          <div>
            <div className="font-medium text-foreground">
              {emptyLog?.message ?? 'No workspace suggestions yet.'}
            </div>
            <div className="mt-1">
              Add a workspace city/state, Google Ads account address, or Brand
              Bible service area, then retry suggestions. Custom search still
              works.
            </div>
            {emptyLog?.detail && (
              <div className="mt-1 text-xs">{emptyLog.detail}</div>
            )}
          </div>
          <Button asChild type="button" size="sm" variant="outline">
            <a href="/workspace-settings?tab=location" className="gap-1.5">
              Open location settings
              <ExternalLink className="h-3 w-3" />
            </a>
          </Button>
        </div>
        <LocationSuggestionProgress progress={100} />
      </div>
    );
  }

  return (
    <div className="rounded border border-dashed border-teal/40 bg-warm-bg">
      {header}
      <ul className="max-h-44 divide-y divide-border overflow-y-auto">
        {suggestions.map((loc) => {
          const isSelected = selectedKeys.has(loc.resource_name);
          return (
            <li key={loc.resource_name}>
              <button
                type="button"
                disabled={isSelected}
                onClick={() => onPick(loc)}
                className={
                  'flex w-full items-center justify-between gap-3 px-3 py-2 text-left ' +
                  (isSelected
                    ? 'cursor-not-allowed bg-card/60 opacity-60'
                    : 'hover:bg-card')
                }
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-teal" />
                    <span className="truncate text-sm font-medium">
                      {loc.name}
                    </span>
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                    {loc.canonical_name}
                  </div>
                  {loc.reason && (
                    <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                      {loc.reason}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <Badge variant="outline" className="text-[10px] font-normal">
                    {isSelected ? 'added' : loc.target_type}
                  </Badge>
                  {loc.reach != null && loc.reach > 0 && (
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      reach {loc.reach.toLocaleString()}
                    </div>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      <LocationSuggestionProgress progress={100} />
    </div>
  );
}


function LocationSuggestionProgress({
  progress,
}: {
  progress: number;
}) {
  const pct = Math.max(0, Math.min(100, Math.round(progress)));
  return (
    <div className="border-t border-teal/20 bg-card/60 px-3 py-2">
      <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>Progress</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-border">
        <div
          className="h-full rounded-full bg-teal transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}


type LocationSearchError = {
  title: string;
  message: string;
  detail?: string;
  actionHref?: string;
  actionLabel?: string;
};


function toLocationSearchError(
  err: unknown,
  tenantId: string,
): LocationSearchError {
  const detail = extractApiErrorDetail(err);
  const normalized = detail.toLowerCase();

  if (
    normalized.includes('google_ad_customer_id')
    || normalized.includes('customer id')
  ) {
    return {
      title: 'Google Ads customer ID required',
      message:
        `${tenantId} is connected to the workspace, but it is not mapped ` +
        'to a Google Ads client account yet. Add the customer ID in ' +
        'Workspace Settings, then search again.',
      detail,
      actionHref: '/workspace-settings?tab=integrations',
      actionLabel: 'Open Google Ads settings',
    };
  }

  if (normalized.includes('oauth token') || normalized.includes('connect google ads')) {
    return {
      title: 'Google Ads connection required',
      message:
        'Connect Google Ads from Workspace Settings before using the ' +
        'Google Ads location catalog.',
      detail,
      actionHref: '/workspace-settings?tab=integrations',
      actionLabel: 'Open Google Ads settings',
    };
  }

  if (
    normalized.includes('timed out')
    || normalized.includes('timeout')
    || normalized.includes('504')
  ) {
    return {
      title: 'Google Ads location search timed out',
      message:
        'The Google Ads location catalog did not respond in time. Try ' +
        'again with a more specific query like "Missouri" or "St. Louis".',
      detail,
    };
  }

  if (
    normalized.includes('network error')
    || normalized.includes('emaxconnsession')
    || normalized.includes('max clients reached')
    || normalized.includes('database connection')
    || normalized.includes('connection pool')
  ) {
    return {
      title: 'Location search is unavailable',
      message:
        'Core is busy and could not look up locations right now. ' +
        'Wait a minute, then retry.',
      detail,
    };
  }

  return {
    title: 'Location search failed',
    message: detail,
  };
}


function extractApiErrorDetail(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    try {
      return JSON.stringify(detail);
    } catch {
      return 'Something went wrong. Try again in a minute.';
    }
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong. Try again in a minute.';
}


function LocationSearchErrorPanel({
  error,
  compact = false,
}: {
  error: LocationSearchError;
  compact?: boolean;
}) {
  return (
    <div className={`space-y-3 bg-warm-bg text-sm ${compact ? 'rounded border border-border p-3' : 'p-4'}`}>
      <div className="flex gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-coral" />
        <div className="space-y-1">
          <div className="font-medium text-coral-dark">{error.title}</div>
          <p className="text-muted-foreground">{error.message}</p>
          {error.detail && error.detail !== error.message && (
            <p className="text-xs text-muted-foreground">{error.detail}</p>
          )}
        </div>
      </div>
      {error.actionHref && error.actionLabel && (
        <Button asChild type="button" size="sm" variant="outline">
          <a href={error.actionHref} className="gap-1.5">
            {error.actionLabel}
            <ExternalLink className="h-3 w-3" />
          </a>
        </Button>
      )}
    </div>
  );
}
